import { z } from "zod";

import { PRODUCT_CATEGORIES, type ProductCategory } from "../categories";
import {
  LIMITS,
  LIST_FONTS,
  LIST_MEMBER_ROLES,
  LIST_OCCASIONS,
  LIST_TEMPLATES,
  LIST_THEMES,
  LIST_VISIBILITIES,
  WISH_PRIORITIES,
} from "../constants";
import { isMotifLayout, isStickerPack } from "../stickers";
import { emailSchema, resourceIdSchema } from "./common";

/** http(s) only: a `javascript:` or `file:` link must never become a wish anyone clicks. */
export const httpUrlSchema = z
  .string()
  .trim()
  .max(LIMITS.urlMax)
  .pipe(z.url({ protocol: /^https?$/, error: "Lien invalide." }));

const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Couleur invalide.");

/** One emoji or a short cluster (flags, skin tones), never a sentence. */
const emojiSchema = z.string().trim().min(1).max(16);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional();

const stickerPackSchema = z.string().refine(isStickerPack, "Pack inconnu.");

export const listAppearanceSchema = z.object({
  template: z.enum(LIST_TEMPLATES).optional(),
  font: z.enum(LIST_FONTS).optional(),
  /** A background motif: one pack drawn in one layout. Null for a plain background. */
  motif: z
    .object({
      pack: stickerPackSchema,
      layout: z.string().refine(isMotifLayout, "Disposition inconnue."),
    })
    .nullable()
    .optional(),
  /** The pack whose stickers decorate the hero. Null for none. */
  stickerPack: stickerPackSchema.nullable().optional(),
});

export const createListSchema = z.object({
  title: z.string().trim().min(1).max(LIMITS.listTitleMax),
  occasion: z.enum(LIST_OCCASIONS),
  description: optionalText(LIMITS.descriptionMax),
  eventDate: z.iso.date().nullable().optional(),
  recipientName: optionalText(LIMITS.nameMax),
  theme: z.enum(LIST_THEMES).optional(),
  /** Overrides the occasion's defaults, merged over them. */
  appearance: listAppearanceSchema.optional(),
});

export const updateListSchema = z.object({
  title: z.string().trim().min(1).max(LIMITS.listTitleMax).optional(),
  occasion: z.enum(LIST_OCCASIONS).optional(),
  description: optionalText(LIMITS.descriptionMax),
  eventDate: z.iso.date().nullable().optional(),
  recipientName: optionalText(LIMITS.nameMax),
  visibility: z.enum(LIST_VISIBILITIES).optional(),
  surprise: z.boolean().optional(),
  theme: z.enum(LIST_THEMES).optional(),
  accentColor: hexColorSchema.nullable().optional(),
  emoji: emojiSchema.nullable().optional(),
  coverUploadId: resourceIdSchema.nullable().optional(),
  appearance: listAppearanceSchema.optional(),
  archived: z.boolean().optional(),
  /**
   * Lifts surprise mode by hand, or puts it back. Only the manual reveal can be undone: past
   * the event day or once archived, the list is revealed whatever this says.
   */
  revealed: z.boolean().optional(),
  showSignature: z.boolean().optional(),
  deliveryAddress: optionalText(LIMITS.deliveryAddressMax),
  fundUrl: httpUrlSchema.nullable().optional(),
});

const priceCentsSchema = z.number().int().min(0).max(100_000_000).nullable().optional();

export const createWishSchema = z.object({
  title: z.string().trim().min(1).max(LIMITS.wishTitleMax),
  url: httpUrlSchema.nullable().optional(),
  note: optionalText(LIMITS.wishNoteMax),
  priceCents: priceCentsSchema,
  priority: z.enum(WISH_PRIORITIES).optional(),
  quantity: z.number().int().min(1).max(LIMITS.wishQuantityMax).optional(),
  /** Set when the wish was picked from the catalogue or a link preview. */
  productId: resourceIdSchema.nullable().optional(),
  imageUploadId: resourceIdSchema.nullable().optional(),
  /** Visible to the list's members only, until the owners publish it. */
  draft: z.boolean().optional(),
});

export const updateWishSchema = createWishSchema.partial();

export const reorderWishesSchema = z.object({
  ids: z.array(resourceIdSchema).min(1).max(LIMITS.wishesPerList),
});

export const inviteListMemberSchema = z.object({
  email: emailSchema,
  role: z.enum(LIST_MEMBER_ROLES).default("owner"),
});

export const acceptListInvitationSchema = z.object({
  token: z.string().min(16).max(256),
});

export const reserveWishSchema = z.object({
  wishId: resourceIdSchema,
  quantity: z.number().int().min(1).max(LIMITS.wishQuantityMax).default(1),
  guestName: optionalText(LIMITS.guestNameMax),
  guestEmail: emailSchema.nullable().optional(),
  /** Other guests still see the name; the list's owners never do. */
  anonymous: z.boolean().default(false),
});

export const cancelReservationSchema = z.object({
  token: z.string().min(16).max(256),
});

export const productPreviewSchema = z.object({ url: httpUrlSchema });

export const productSearchSchema = z.object({
  q: z.string().trim().min(2).max(LIMITS.searchQueryMax),
});

export const assistantRequestSchema = z.object({
  messages: z
    .array(
      z.object({
        role: z.enum(["user", "assistant"]),
        content: z.string().trim().min(1).max(LIMITS.assistantMessageMax),
      }),
    )
    .min(1)
    .max(LIMITS.assistantHistoryMax)
    .refine(
      (messages) => messages.at(-1)?.role === "user",
      "Le dernier message doit venir de vous.",
    ),
});

export const shareImageSchema = z.object({
  format: z.enum(["og", "story", "square"]).default("og"),
});

export type CreateListInput = z.input<typeof createListSchema>;
export type UpdateListInput = z.input<typeof updateListSchema>;
export type ListAppearanceInput = z.infer<typeof listAppearanceSchema>;
export type CreateWishInput = z.input<typeof createWishSchema>;
export type UpdateWishInput = z.input<typeof updateWishSchema>;
export type InviteListMemberInput = z.input<typeof inviteListMemberSchema>;
export type ReserveWishInput = z.input<typeof reserveWishSchema>;
export type AssistantRequestInput = z.input<typeof assistantRequestSchema>;

export const reviewSchema = z.object({
  rating: z.number().int().min(1).max(5),
  body: optionalText(LIMITS.reviewBodyMax),
});

export const catalogueQuerySchema = z.object({
  q: z.string().trim().max(LIMITS.searchQueryMax).optional(),
  occasion: z.enum(LIST_OCCASIONS).optional(),
  category: z.enum(PRODUCT_CATEGORIES as [ProductCategory, ...ProductCategory[]]).optional(),
  sort: z.enum(["popular", "rating", "trend"]).default("popular"),
  cursor: z.string().max(64).optional(),
});

export const catalogueCategoriesQuerySchema = z.object({
  occasion: z.enum(LIST_OCCASIONS).optional(),
});

export type ReviewInput = z.input<typeof reviewSchema>;
export type CatalogueQuery = z.input<typeof catalogueQuerySchema>;
export type CatalogueCategoriesQuery = z.input<typeof catalogueCategoriesQuerySchema>;
